import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { fetchSpots } from '../../util/api';
import { SPOT_FILTERS, filterSpots, withDistance } from '../../util/spots';
import { FieldLocationAutocompleteInput, LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SpotsMap from './SpotsMap';

import css from './OnLocationPage.module.css';

const formatWhen = (intl, start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const day = intl.formatDate(s, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d => intl.formatTime(d, { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time(s)} – ${time(e)}`;
};

const identity = v => v;

const OnLocationPage = () => {
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const [spots, setSpots] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  // Where the visitor wants to look: a searched place or their own position. It only centres the
  // map and sorts the list in the browser; it is never sent to the server.
  const [origin, setOrigin] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateFailed, setLocateFailed] = useState(false);
  const formApi = useRef(null);

  useEffect(() => {
    fetchSpots()
      .then(response => setSpots(response?.spots || []))
      .catch(() => setFailed(true));
  }, []);

  // Evaluated again whenever the list or the filter changes, so a spot that ends while the page is
  // open disappears the next time the visitor touches a filter.
  const visible = useMemo(() => filterSpots(spots, filter), [spots, filter]);
  const ordered = useMemo(() => (origin ? withDistance(visible, origin) : visible), [visible, origin]);

  // A place picked in the search field becomes the origin. Emptying the field removes it again,
  // unless the origin is the visitor's own position.
  const onSearchChange = useCallback(value => {
    const place = value && value.selectedPlace;
    if (place && place.origin) {
      setLocateFailed(false);
      setOrigin({ lat: place.origin.lat, lng: place.origin.lng, label: place.address || '', own: false });
    } else if (!value || !value.search) {
      setOrigin(current => (current && current.own ? current : null));
    }
  }, []);

  const locateMe = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocateFailed(true);
      return;
    }
    setLocating(true);
    setLocateFailed(false);
    navigator.geolocation.getCurrentPosition(
      position => {
        setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude, label: '', own: true });
        setLocating(false);
        if (formApi.current) formApi.current.change('location', undefined);
      },
      () => {
        setLocateFailed(true);
        setLocating(false);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  const clearOrigin = useCallback(() => {
    setOrigin(null);
    setLocateFailed(false);
    if (formApi.current) formApi.current.change('location', undefined);
  }, []);

  // The popup is a DOM node (not HTML text): the photographer's note is shown as plain text.
  const buildPopup = useCallback(
    spot => {
      const el = document.createElement('div');
      el.className = css.popup;
      const add = (tag, text, className) => {
        const child = document.createElement(tag);
        child.textContent = text;
        if (className) child.className = className;
        el.appendChild(child);
        return child;
      };
      add('strong', spot.authorName || '');
      add('div', spot.placeLabel);
      add('div', formatWhen(intl, spot.start, spot.end), css.muted);
      if (spot.note) add('div', spot.note);
      const link = add('a', intl.formatMessage({ id: 'OnLocationPage.viewProfile' }), css.popupLink);
      link.href = `/u/${spot.authorId}`;
      return el;
    },
    [intl]
  );

  const onSelect = useCallback(id => setSelectedId(id), []);

  return (
    <Page
      title={intl.formatMessage({ id: 'OnLocationPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="OnLocationPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <h1 className={css.heading}>
            <FormattedMessage id="OnLocationPage.heading" />
          </h1>
          <p className={css.intro}>
            <FormattedMessage id="OnLocationPage.intro" />
          </p>

          <div className={css.search}>
            <FinalForm
              onSubmit={() => {}}
              render={({ form }) => {
                formApi.current = form;
                return (
                  <form className={css.searchForm} onSubmit={event => event.preventDefault()}>
                    <FormSpy subscription={{ values: true }} onChange={({ values }) => onSearchChange(values.location)} />
                    <FieldLocationAutocompleteInput
                      rootClassName={css.searchField}
                      inputClassName={css.searchInput}
                      iconClassName={css.searchIcon}
                      predictionsClassName={css.predictions}
                      name="location"
                      label={intl.formatMessage({ id: 'OnLocationPage.searchLabel' })}
                      labelClassName={css.searchLabel}
                      placeholder={intl.formatMessage({ id: 'OnLocationPage.searchPlaceholder' })}
                      useDefaultPredictions={false}
                      format={identity}
                      hideErrorMessage
                    />
                  </form>
                );
              }}
            />
            <div className={css.searchActions}>
              <button type="button" className={css.nearMe} onClick={locateMe} disabled={locating}>
                <FormattedMessage id={locating ? 'OnLocationPage.locating' : 'OnLocationPage.nearMe'} />
              </button>
              {origin ? (
                <button type="button" className={css.clear} onClick={clearOrigin}>
                  <FormattedMessage id="OnLocationPage.clearSearch" />
                </button>
              ) : null}
            </div>
          </div>
          {locateFailed ? (
            <p className={css.error}>
              <FormattedMessage id="OnLocationPage.locateFailed" />
            </p>
          ) : null}
          {origin ? (
            <p className={css.muted}>
              {origin.own ? (
                <FormattedMessage id="OnLocationPage.sortedByOwnPosition" />
              ) : (
                <FormattedMessage id="OnLocationPage.sortedByPlace" values={{ place: origin.label }} />
              )}
            </p>
          ) : null}

          <div className={css.filters} role="group" aria-label={intl.formatMessage({ id: 'OnLocationPage.filterLabel' })}>
            {SPOT_FILTERS.map(name => (
              <button
                key={name}
                type="button"
                className={classNames(css.filter, { [css.filterActive]: filter === name })}
                aria-pressed={filter === name}
                onClick={() => setFilter(name)}
              >
                <FormattedMessage id={`OnLocationPage.filter.${name}`} />
              </button>
            ))}
          </div>

          <SpotsMap
            spots={ordered}
            origin={origin}
            selectedId={selectedId}
            onSelect={onSelect}
            buildPopup={buildPopup}
          />

          {failed ? (
            <p className={css.error}>
              <FormattedMessage id="OnLocationPage.loadFailed" />
            </p>
          ) : spots === null ? (
            <p className={css.muted}>
              <FormattedMessage id="OnLocationPage.loading" />
            </p>
          ) : visible.length === 0 ? (
            <p className={css.muted}>
              <FormattedMessage id="OnLocationPage.empty" />
            </p>
          ) : (
            <ul className={css.list}>
              {ordered.map(spot => (
                <li
                  key={spot.id}
                  className={classNames(css.spot, { [css.spotSelected]: selectedId === spot.id })}
                >
                  <button type="button" className={css.spotButton} onClick={() => setSelectedId(spot.id)}>
                    <span className={css.spotName}>{spot.authorName}</span>
                    <span>{spot.placeLabel}</span>
                    <span className={css.muted}>{formatWhen(intl, spot.start, spot.end)}</span>
                    {typeof spot.distanceKm === 'number' ? (
                      <span className={css.distance}>
                        <FormattedMessage
                          id="OnLocationPage.distanceAway"
                          values={{ distance: intl.formatNumber(spot.distanceKm, { maximumFractionDigits: 1 }) }}
                        />
                      </span>
                    ) : null}
                    {spot.exact ? null : (
                      <span className={css.muted}>
                        <FormattedMessage id="OnLocationPage.approximate" />
                      </span>
                    )}
                    {spot.note ? <span>{spot.note}</span> : null}
                  </button>
                  <NamedLink name="ProfilePage" params={{ id: spot.authorId }} className={css.profileLink}>
                    <FormattedMessage id="OnLocationPage.viewProfile" />
                  </NamedLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default OnLocationPage;
